import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { PUBLIC_BASE_URL } from "@/config/site";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isWaiter: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isWaiter, setIsWaiter] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkRoles = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    if (!data) return { isAdmin: false, isSuperAdmin: false, isWaiter: false };

    return {
      isAdmin: data.some(r => r.role === 'admin' || r.role === 'super_admin'),
      isSuperAdmin: data.some(r => r.role === 'super_admin'),
      isWaiter: data.some(r => r.role === 'garcom' || r.role === 'admin' || r.role === 'super_admin'),
    };
  };

  useEffect(() => {
    let mounted = true;

    const applySession = (nextSession: Session | null) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsWaiter(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsWaiter(false);

      void checkRoles(nextSession.user.id)
        .then((roles) => {
          if (!mounted) return;
          setIsAdmin(roles.isAdmin);
          setIsSuperAdmin(roles.isSuperAdmin);
          setIsWaiter(roles.isWaiter);
        })
        .finally(() => {
          if (!mounted) return;
          setLoading(false);
        });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        applySession(nextSession);
      }
    );

    supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      if (!mounted) return;
      applySession(nextSession);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${PUBLIC_BASE_URL}/planos?checkout=continue` },
    });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, isSuperAdmin, isWaiter, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
